import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Label,
  Input,
  CardHeader,
  FormFeedback,
  Form,
  Alert,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { DebounceInput } from "react-debounce-input";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getComplains,
  sendReplyToComplain,
  toggleComplainReplyModal,
} from "../../store/complains/actions";

const ComplainsPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    // complains
    complains,
    loadingComplains,
  } = useSelector((state) => state.Complains);

  document.title = `${t("Complains.Complains")} | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  const [isComplainModalOpen, setIsComplainModalOpen] = useState(false);

  const toggleComplainModal = () => {
    if (!isComplainModalOpen) {
      dispatch(toggleComplainReplyModal());
      // reset on modal open
      complainReplyForm.resetForm({
        title: "",
        arabicTitle: "",
        body: "",
        arabicBody: "",
      });
    }
    setIsComplainModalOpen(!isComplainModalOpen);
  };

  const [request, setRequest] = useState({
    limit: 10,
    page: 1,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const [totalRows, setTotalRows] = useState(0);
  const [selectedComplain, setSelectedComplain] = useState();

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.Type")}</span>,
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">{row.type}</div>
        </div>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.From")}</span>,
      selector: (row) => {
        if (row.user) {
          return (
            <div className="py-1">
              <span className="mb-1">
                {row.user.firstName + " " + row.user.lastName}
              </span>
              <br />
              <span className="mt-1 small">{t(`Roles.${row.user.role}`)}</span>
              <br />
              <span className="mt-1 small">
                {row.user.email ? (
                  <a href={`mailto:${row.user.email}`} title={row.user.email}>
                    {row.user.email}
                  </a>
                ) : (
                  " - "
                )}
              </span>
              <br />
              <span className="mt-1 small">
                <a href={`tel:${row.user.mobile}`} title={row.user.mobile}>
                  {row.user.mobile}
                </a>
              </span>
            </div>
          );
        } else {
          return "-";
        }
      },
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Fields.Message")}</span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            <a
              style={{ cursor: "pointer" }}
              className="link"
              title={t("Common.View Message")}
              onClick={() => {
                setSelectedComplain(row);
                toggleComplainModal();
              }}
            >
              {t("Common.View Message")}
            </a>
          </div>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Created At")}</span>
      ),
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
  ];

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let query = {
      limit: queryParams.has("limit") ? queryParams.get("limit") : 10,
      page: queryParams.has("page") ? queryParams.get("page") : 1,
      sortBy: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[0]
        : "createdAt",
      sortDirection: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[1]
        : "DESC",
      search: queryParams.has("search") ? queryParams.get("search") : "",
    };
    setDefaultSortAsc(query.sortDirection === "ASC");
    setDefaultSortFieldId(getSortByColumnId(query.sortBy));
    setRequest(query);
    dispatch(getComplains(query));
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, sortDirection } = request;
    let query;
    if (page) query = `?page=${page}`;
    if (limit) query += `&limit=${limit}`;
    if (search && search !== "") {
      history.replace({
        search: `&search=${search}`,
      });
    }
    if (sortBy && sortDirection) query += `&sortBy=${sortBy}:${sortDirection}`;
    if (search) query += `&search=${search}`;
    history.push({ search: query });
  }, [request]);

  useEffect(() => {
    setRows(complains?.data);
    setTotalRows(complains.meta?.totalItems);
  }, [complains, loadingComplains]);

  const onChangeRowsPerPage = (pageSize) => {
    setRequest({ ...request, limit: pageSize });
    dispatch(getComplains({ ...request, limit: pageSize }));
  };

  const onChangePage = (page) => {
    setRequest({ ...request, page: page });
    dispatch(getComplains({ ...request, page: page }));
  };

  const onSearch = (text) => {
    setRequest({ ...request, search: text });
    dispatch(getComplains({ ...request, search: text }));
  };

  const getSortByColumnName = (id) => {
    let sortBy;
    if (i18n.language === "en") {
      switch (id) {
        case 1:
          sortBy = "type";
          break;
        default:
          sortBy = "createdAt";
          break;
      }
    }
    if (i18n.language === "ar") {
      switch (id) {
        case 1:
          sortBy = "type";
          break;
        default:
          sortBy = "createdAt";
          break;
      }
    }
    return sortBy;
  };

  const getSortByColumnId = (name) => {
    let sortBy;
    switch (name) {
      case "type":
        sortBy = 1;
        break;
      default:
        sortBy = 4;
        break;
    }
    return sortBy;
  };

  const onSort = (column, sortDirection) => {
    const sortBy = getSortByColumnName(column.id);
    setRequest({
      ...request,
      sortBy: sortBy,
      sortDirection: sortDirection.toUpperCase(),
    });
    dispatch(
      getComplains({
        ...request,
        sortDirection: sortDirection.toUpperCase(),
        sortBy: sortBy,
      })
    );
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(4);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);

  //#region notification
  const {
    sendReplyToComplainLoading,
    sendReplyToComplainError,
    sendReplyToComplainSuccess,
    reply,
  } = useSelector((state) => state.Complains);

  useEffect(() => {
    if (sendReplyToComplainSuccess) {
      toggleComplainModal();
    }
  }, [sendReplyToComplainSuccess]);

  /**
   * Formik handling
   */
  const complainReplyForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: reply.title || "",
      arabicTitle: reply.arabicTitle || "",
      body: reply.body || "",
      arabicBody: reply.arabicBody || "",
    },

    validationSchema: Yup.object({
      title: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.Title.Required")),
      arabicTitle: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.ArabicTitle.Required")),
      body: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.Body.Required")),
      arabicBody: Yup.string()
        .max(255, t("Validations.Common.Max", { length: 255 }))
        .required(t("Validations.ArabicBody.Required")),
    }),

    onSubmit: (values) => {
      dispatch(
        sendReplyToComplain({ ...values, complainId: selectedComplain.id })
      );
    },
  });

  const getTitle = () => {
    if (
      selectedComplain &&
      selectedComplain.type &&
      selectedComplain.user &&
      selectedComplain.user.role
    ) {
      var type = selectedComplain.type.toLowerCase();
      var role = selectedComplain.user.role.toLowerCase();
      if (type === "complain" && role === "customer") {
        return t("Messages.Information.Complaint from Customer");
      } else if (type === "suggestion" && role === "customer") {
        return t("Messages.Information.Suggestion from Customer");
      } else if (type === "complain" && role === "technician") {
        return t("Messages.Information.Complaint from Technician");
      } else if (type === "suggestion" && role === "technician") {
        return t("Messages.Information.Suggestion from Technician");
      }
    } else {
      return t("Common.Complain Detail");
    }
  };

  //#endregion

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Complains.Complains")}
            pageTitle={t("Common.Users")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-sm-auto">
                      <div className="">
                        <div className="search-box ms-2">
                          <DebounceInput
                            className="form-control search"
                            placeholder={`${t("Common.Search")}...`}
                            minLength={2}
                            debounceTimeout={300}
                            onChange={(event) => onSearch(event.target.value)}
                            value={request.search}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loadingComplains}
                    progressComponent={
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    }
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    defaultSortAsc={defaultSortAsc}
                    persistTableHead
                    paginationTotalRows={totalRows}
                    paginationComponentOptions={{
                      rowsPerPageText: t("Pagination.Rows per page"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Detail & Reply Modal */}
      <Modal
        isOpen={isComplainModalOpen}
        toggle={() => {
          toggleComplainModal();
        }}
        centered
        size="xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            complainReplyForm.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader className="bg-light p-3">
            {getTitle()}
            <Button
              type="button"
              onClick={() => {
                setSelectedComplain(null);
                setIsComplainModalOpen(false);
              }}
              className="btn-close"
              aria-label={t("Common.Close")}
            ></Button>
          </ModalHeader>
          <ModalBody>
            <Card>
              <CardHeader>
                <h5>{t("Fields.Message")}</h5>
                <small>{t("Messages.Information.Message from User")}</small>
              </CardHeader>
              <CardBody>
                <p className="text-justified">{selectedComplain?.message}</p>
              </CardBody>
            </Card>
            {sendReplyToComplainError && sendReplyToComplainError ? (
              <Alert color="danger">
                {" "}
                {t(`Messages.Error.${sendReplyToComplainError}`)}{" "}
              </Alert>
            ) : null}
            <Card>
              <CardHeader>
                <h5>{t("Common.Reply via Push Notification")}</h5>
                <small>
                  {t(
                    "Messages.Information.A push notification will be sent to user devices"
                  )}
                </small>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="title" className="form-label">
                        {t("Fields.Title")}*
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="title"
                        placeholder={t("Fields.Enter Title")}
                        value={complainReplyForm.values.title || ""}
                        onChange={complainReplyForm.handleChange}
                        onBlur={complainReplyForm.handleBlur}
                        invalid={
                          complainReplyForm.touched.title &&
                          complainReplyForm.errors.title
                            ? true
                            : false
                        }
                      />
                      {complainReplyForm.touched.title &&
                      complainReplyForm.errors.title ? (
                        <FormFeedback type="invalid">
                          {complainReplyForm.errors.title}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="arabicTitle" className="form-label">
                        {t("Fields.Arabic Title")}*
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="arabicTitle"
                        placeholder={t("Fields.Enter Arabic Title")}
                        value={complainReplyForm.values.arabicTitle || ""}
                        onChange={complainReplyForm.handleChange}
                        onBlur={complainReplyForm.handleBlur}
                        invalid={
                          complainReplyForm.touched.arabicTitle &&
                          complainReplyForm.errors.arabicTitle
                            ? true
                            : false
                        }
                      />
                      {complainReplyForm.touched.arabicTitle &&
                      complainReplyForm.errors.arabicTitle ? (
                        <FormFeedback type="invalid">
                          {complainReplyForm.errors.arabicTitle}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="body" className="form-label">
                        {t("Fields.Body")}*
                      </Label>
                      <Input
                        type="textarea"
                        row="2"
                        className="form-control"
                        id="body"
                        placeholder={t("Fields.Enter message body")}
                        value={complainReplyForm.values.body || ""}
                        onChange={complainReplyForm.handleChange}
                        onBlur={complainReplyForm.handleBlur}
                        invalid={
                          complainReplyForm.touched.body &&
                          complainReplyForm.errors.body
                            ? true
                            : false
                        }
                      />
                      {complainReplyForm.touched.body &&
                      complainReplyForm.errors.body ? (
                        <FormFeedback type="invalid">
                          {complainReplyForm.errors.body}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col>
                    <div className="mb-3">
                      <Label htmlFor="arabicBody" className="form-label">
                        {t("Fields.Arabic Body")}*
                      </Label>
                      <Input
                        type="textarea"
                        row="2"
                        className="form-control"
                        id="arabicBody"
                        placeholder={t("Fields.Enter message body in arabic")}
                        value={complainReplyForm.values.arabicBody || ""}
                        onChange={complainReplyForm.handleChange}
                        onBlur={complainReplyForm.handleBlur}
                        invalid={
                          complainReplyForm.touched.arabicBody &&
                          complainReplyForm.errors.arabicBody
                            ? true
                            : false
                        }
                      />
                      {complainReplyForm.touched.arabicBody &&
                      complainReplyForm.errors.arabicBody ? (
                        <FormFeedback type="invalid">
                          {complainReplyForm.errors.arabicBody}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  setSelectedComplain(null);
                  setIsComplainModalOpen(false);
                }}
              >
                {t("Common.Close")}
              </button>
              <button
                type="submit"
                className="btn btn-success"
                id="add-btn"
                disabled={sendReplyToComplainLoading}
              >
                {sendReplyToComplainLoading ? (
                  <span className="d-flex align-items-center">
                    <span className="flex-grow-1 me-2">
                      {t("Common.Sending")}...
                    </span>
                    <Spinner size="sm" className="flex-shrink-0" role="status">
                      {" "}
                      {t("Common.Sending")}
                      ...{" "}
                    </Spinner>
                  </span>
                ) : (
                  <>{t("Common.Send Reply")}</>
                )}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default ComplainsPage;
